import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { User, Mail, Lock, CreditCard, Save, Loader2, Gift, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getSupabaseClient, API_URL, publicAnonKey } from '../utils/supabaseClient';

// Use singleton Supabase client
const supabase = getSupabaseClient();

export function ProfileSettings() {
  const { user, credits, refreshUser, refreshCredits } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  
  // Accordion state
  const [openSection, setOpenSection] = useState<'profile' | 'password' | 'credits' | null>('profile');

  const toggleSection = (section: 'profile' | 'password' | 'credits') => {
    setOpenSection(openSection === section ? null : section);
  };

  const handleUpdateName = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      console.log('🔐 Update Profile - Session check:', {
        hasSession: !!session,
        hasAccessToken: !!session?.access_token,
        tokenLength: session?.access_token?.length,
        user: session?.user?.email
      });

      if (!session?.access_token) {
        toast.error('Session expired. Please login again.');
        setIsLoading(false);
        return;
      }

      const url = `${API_URL}/auth/update-profile`;
      console.log('📡 Sending request to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
          'X-Auth-Token': session.access_token,
        },
        body: JSON.stringify({ name }),
      });

      console.log('📬 Response status:', response.status);

      if (response.ok) {
        await refreshUser();
        toast.success('Profile updated successfully');
      } else {
        const error = await response.json();
        console.error('❌ Update profile error response:', error);
        toast.error(error.message || error.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('❌ Update profile exception:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    // Strong password validation
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    
    if (!/[a-z]/.test(newPassword)) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    
    if (!/[0-9]/.test(newPassword)) {
      toast.error('Password must contain at least one number');
      return;
    }

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        toast.error('Session expired. Please login again.');
        setIsLoading(false);
        return;
      }

      const url = `${API_URL}/auth/change-password`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
          'X-Auth-Token': session.access_token,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (response.ok) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        toast.success('Password changed successfully');
      } else {
        const error = await response.json();
        console.error('Change password error response:', error);
        toast.error(error.message || error.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Change password error:', error);
      toast.error('Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Promo code cannot be empty');
      return;
    }

    setApplyingPromo(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        toast.error('Session expired. Please login again.');
        setApplyingPromo(false);
        return;
      }

      const url = `${API_URL}/promo-codes/apply`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
          'X-Auth-Token': session.access_token,
        },
        body: JSON.stringify({ code: promoCode }),
      });

      if (response.ok) {
        const result = await response.json();
        setPromoCode('');
        await refreshCredits();
        toast.success(result.message || 'Promo code applied successfully!');
      } else {
        const error = await response.json();
        console.error('Apply promo code error response:', error);
        toast.error(error.error || 'Invalid or expired promo code');
      }
    } catch (error) {
      console.error('Apply promo code error:', error);
      toast.error('Failed to apply promo code');
    } finally {
      setApplyingPromo(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Profile Settings</h1>
          <p className="text-slate-600">Manage your account settings and preferences</p>
        </div>

        <div className="grid gap-4">
          {/* Profile Information Accordion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
          >
            {/* Accordion Header */}
            <button
              onClick={() => toggleSection('profile')}
              className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#4F819F]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#4F819F]" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900">Profile Information</h2>
                  <p className="text-sm text-slate-600">Update your personal details</p>
                </div>
              </div>
              <motion.div
                animate={{ rotate: openSection === 'profile' ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </motion.div>
            </button>

            {/* Accordion Content */}
            <AnimatePresence>
              {openSection === 'profile' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 space-y-4 border-t border-slate-100">
                    <div className="pt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#4F819F]/50 focus:border-[#4F819F]"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email
                      </label>
                      <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-slate-50">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600">{user?.email}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                    </div>

                    <button
                      onClick={handleUpdateName}
                      disabled={isLoading || name === user?.name}
                      className="flex items-center gap-2 px-4 py-2 bg-[#4F819F] text-white rounded-lg hover:bg-[#4E7F9D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Changes
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Change Password Accordion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
          >
            {/* Accordion Header */}
            <button
              onClick={() => toggleSection('password')}
              className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#4F819F]/10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-[#4F819F]" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900">Change Password</h2>
                  <p className="text-sm text-slate-600">Update your password to keep your account secure</p>
                </div>
              </div>
              <motion.div
                animate={{ rotate: openSection === 'password' ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </motion.div>
            </button>

            {/* Accordion Content */}
            <AnimatePresence>
              {openSection === 'password' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 space-y-4 border-t border-slate-100">
                    <div className="pt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#4F819F]/50 focus:border-[#4F819F]"
                        placeholder="Enter current password"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#4F819F]/50 focus:border-[#4F819F]"
                        placeholder="Enter new password"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#4F819F]/50 focus:border-[#4F819F]"
                        placeholder="Confirm new password"
                      />
                    </div>

                    <button
                      onClick={handleChangePassword}
                      disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
                      className="flex items-center gap-2 px-4 py-2 bg-[#4F819F] text-white rounded-lg hover:bg-[#4E7F9D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Lock className="w-4 h-4" />
                      )}
                      Change Password
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Credits Information Accordion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
          >
            {/* Accordion Header */}
            <button
              onClick={() => toggleSection('credits')}
              className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#4F819F]/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#4F819F]" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900">Credits & Coupons</h2>
                  <p className="text-sm text-slate-600">Manage your credits and apply promo codes</p>
                </div>
              </div>
              <motion.div
                animate={{ rotate: openSection === 'credits' ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </motion.div>
            </button>

            {/* Accordion Content */}
            <AnimatePresence>
              {openSection === 'credits' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 space-y-4 border-t border-slate-100">
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="bg-gradient-to-br from-[#4F819F]/10 to-[#4E7F9D]/10 rounded-lg p-4 border border-[#4F819F]/20">
                        <p className="text-sm text-slate-600 mb-1">Available Credits</p>
                        <p className="text-3xl font-bold text-[#4F819F]">{credits?.balance || 0}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <p className="text-sm text-slate-600 mb-1">Total Used</p>
                        <p className="text-3xl font-bold text-slate-700">{credits?.totalUsed || 0}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-900">
                        💡 Each analysis costs 1 credit. Purchase more credits to continue creating analyses.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Promo Code
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#4F819F]/50 focus:border-[#4F819F]"
                          placeholder="Enter promo code"
                        />
                        <button
                          onClick={applyPromoCode}
                          disabled={applyingPromo || !promoCode}
                          className="flex items-center gap-2 px-4 py-2 bg-[#4F819F] text-white rounded-lg hover:bg-[#4E7F9D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {applyingPromo ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Gift className="w-4 h-4" />
                          )}
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}